/**
 * Просмотры страниц при клиентской навигации.
 *
 * Счётчик фиксирует только первую загрузку: Метрика отправляет хит на `init`, а
 * дальше переходы идут через History API и мимо неё. Поэтому переходы отдаём
 * сами — и ровно один раз: двойной счёт здесь единственный реальный класс
 * ошибок, поэтому логика вынесена из точки входа и покрыта тестами.
 */

import { trackPageView } from './web-analytics';
import type { AnyRouter } from '@tanstack/react-router';

/** `/business/` и `/business` — одна страница; корень остаётся `/`. */
function normalizePathname(pathname: string): string {
  const trimmed = pathname.replace(/\/+$/, '');
  return trimmed === '' ? '/' : trimmed;
}

/**
 * Подписывает аналитику на разрешённые маршруты. Возвращает функцию отписки.
 *
 * Первое разрешение приходится на адрес, который счётчик уже посчитал при
 * инициализации, поэтому оно пропускается. Сравнение идёт по нормализованному
 * пути: заход на `/business/` роутер разрешает в `/business`, и без нормализации
 * такой визит дал бы два просмотра одной страницы.
 *
 * Источником перехода считается последняя посчитанная страница, а не предыдущее
 * разрешение маршрута: адрес входа с utm-метками так остаётся источником для
 * первого внутреннего перехода.
 *
 * Метаданные подписываются на `onResolved` раньше аналитики (см. `main.tsx`),
 * поэтому к этому моменту title уже соответствует новому маршруту и языку.
 */
export function subscribeToRouteChanges(
  router: AnyRouter,
  initialHref: string = location.href,
): () => void {
  let previous = new URL(initialHref, location.origin);

  return router.subscribe('onResolved', ({ toLocation }) => {
    // `toLocation.href` — путь с query и хэшем, но без origin.
    const next = new URL(toLocation.href, location.origin);
    if (normalizePathname(next.pathname) === normalizePathname(previous.pathname)) return;

    const referrer = previous.href;
    previous = next;
    trackPageView({ url: next.href, referrer, title: document.title });
  });
}
